import {useSelector} from "react-redux";
import _ from 'lodash';
import SiteRow from "./SiteRow";
import {AlertCircle, Filter, Globe, Loader2, RefreshCw, Search, Table} from "lucide-react";
import {useMemo, useState} from "react";

export default function SitesTable() {
    const sites = useSelector(state => state.sites.items);
    const loading = useSelector(state => state.sites.loading);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [sortBy, setSortBy] = useState("name");

    // Vérification de sécurité avec lodash
    const safeSites = _.isArray(sites) ? sites : [];

    // Filtrer et trier les sites avec lodash
    const filteredSites = useMemo(() => {
        let result = [...safeSites];

        // Filtre par recherche
        if (searchTerm) {
            result = _.filter(result, site => {
                const name = _.get(site, 'name', '').toLowerCase();
                const url = _.get(site, 'url', '').toLowerCase();
                return name.includes(searchTerm.toLowerCase()) ||
                    url.includes(searchTerm.toLowerCase());
            });
        }

        // Filtre par statut
        if (statusFilter !== "all") {
            result = _.filter(result, site => _.get(site, 'status') === statusFilter);
        }

        // Tri avec lodash
        switch (sortBy) {
            case "name":
                result = _.sortBy(result, site => _.get(site, 'name', '').toLowerCase());
                break;
            case "visits":
                result = _.orderBy(result,
                    [site => _.toNumber(_.get(site, 'todayVisites', 0))],
                    ['desc']
                );
                break;
            case "status":
                const statusOrder = { active: 1, warning: 2, error: 3, inactive: 4 };
                result = _.sortBy(result, site => statusOrder[_.get(site, 'status', 'inactive')]);
                break;
            default:
                break;
        }

        return result;
    }, [safeSites, searchTerm, statusFilter, sortBy]);

    // Calcul des statistiques de filtrage
    const stats = {
        total: safeSites.length,
        active: _.filter(safeSites, s => _.get(s, 'status') === 'active').length,
        filtered: filteredSites.length
    };

    if (loading) {
        return (
            <div className="bg-white rounded-xl shadow p-8 flex flex-col items-center justify-center">
                <Loader2 className="h-12 w-12 text-blue-500 animate-spin mb-4" />
                <p className="text-gray-600">Chargement des sites...</p>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl shadow overflow-hidden">
            {/* En-tête avec filtres */}
            <div className="p-6 border-b border-gray-200">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                            <Table className="h-5 w-5 mr-2" />
                            Sites Surveillés
                        </h2>
                        <p className="text-sm text-gray-500 mt-1">
                            {stats.filtered} site{stats.filtered !== 1 ? 's' : ''} affiché{stats.filtered !== 1 ? 's' : ''}
                            {stats.filtered !== stats.total && ` sur ${stats.total}`}
                        </p>
                    </div>

                    <div className="flex flex-wrap gap-3">
                        {/* Barre de recherche */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Rechercher un site..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                            />
                        </div>

                        {/* Filtre par statut */}
                        <div className="flex items-center">
                            <Filter className="h-4 w-4 text-gray-400 mr-2" />
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                            >
                                <option value="all">Tous les statuts</option>
                                <option value="active">Actif</option>
                                <option value="inactive">Inactif</option>
                                <option value="warning">Avertissement</option>
                                <option value="error">Erreur</option>
                            </select>
                        </div>

                        {/* Tri */}
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        >
                            <option value="name">Trier par nom</option>
                            <option value="visits">Trier par visites</option>
                            <option value="status">Trier par statut</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Tableau */}
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-gray-50">
                    <tr>
                        <th className="text-left py-4 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <div className="flex items-center">
                                <Globe className="h-4 w-4 mr-2" />
                                Site
                            </div>
                        </th>
                        <th className="text-left py-4 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Statut
                        </th>
                        <th className="text-left py-4 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                            En ligne
                        </th>
                        <th className="text-left py-4 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Visites aujourd'hui
                        </th>
                        <th className="text-left py-4 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Total visites
                        </th>
                        <th className="text-left py-4 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                        </th>
                    </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                    {filteredSites.length > 0 ? (
                        filteredSites.map((site) => (
                            <SiteRow key={_.get(site, 'id') || _.get(site, 'siteKey')} site={site} />
                        ))
                    ) : (
                        <tr>
                            <td colSpan="6" className="py-12 text-center">
                                <div className="flex flex-col items-center">
                                    <AlertCircle className="h-12 w-12 text-gray-400 mb-4" />
                                    <p className="text-gray-500 text-lg font-medium mb-2">
                                        Aucun site trouvé
                                    </p>
                                    <p className="text-gray-400 text-sm">
                                        {searchTerm || statusFilter !== "all"
                                            ? "Essayez de modifier vos filtres de recherche"
                                            : "Aucun site n'est configuré pour le moment"}
                                    </p>
                                </div>
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>

            {/* Pied de tableau */}
            {filteredSites.length > 0 && (
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-500">
                            Affichage de 1 à {filteredSites.length} sur {stats.total} sites
                        </div>
                        <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2">
                                <div className="flex items-center">
                                    <div className="h-2 w-2 rounded-full bg-green-500 mr-2"></div>
                                    <span className="text-xs text-gray-600">
                    {stats.active} actif{stats.active !== 1 ? 's' : ''}
                  </span>
                                </div>
                                <div className="flex items-center">
                                    <div className="h-2 w-2 rounded-full bg-red-500 mr-2"></div>
                                    <span className="text-xs text-gray-600">
                    {stats.total - stats.active} inactif{stats.total - stats.active !== 1 ? 's' : ''}
                  </span>
                                </div>
                            </div>
                            <button className="flex items-center text-sm text-blue-600 hover:text-blue-800">
                                <RefreshCw className="h-4 w-4 mr-1" />
                                Actualiser
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
